import type React from 'react';
import { useSyncExternalStore } from 'react';

const EVENT = 'airpost:navigate';

function snapshot(): string {
  return window.location.pathname + window.location.search;
}

function subscribe(cb: () => void): () => void {
  window.addEventListener('popstate', cb);
  window.addEventListener(EVENT, cb);
  return () => {
    window.removeEventListener('popstate', cb);
    window.removeEventListener(EVENT, cb);
  };
}

export function useLocation(): { path: string; query: URLSearchParams; href: string } {
  const href = useSyncExternalStore(subscribe, snapshot, snapshot);
  const url = new URL(href, window.location.origin);
  return { path: url.pathname, query: url.searchParams, href };
}

let leaveGuard: ((to: string) => boolean) | null = null;

/** A page holding unsaved work answers whether the app may leave it for `to` (it asks the user); null when none does. */
export function setLeaveGuard(guard: ((to: string) => boolean) | null): void {
  leaveGuard = guard;
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (leaveGuard && !leaveGuard(to)) return;
  if (opts.replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  window.dispatchEvent(new Event(EVENT));
}

/** Match '/rundown/:id' style patterns. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/');
  const s = path.split('/');
  if (p.length !== s.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i]!.startsWith(':')) params[p[i]!.slice(1)] = decodeURIComponent(s[i]!);
    else if (p[i] !== s[i]) return null;
  }
  return params;
}

export function Link(props: React.AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  const { to, onClick, ...rest } = props;
  return (
    <a
      href={to}
      {...rest}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to);
      }}
    />
  );
}
